import "./index.css"

import { InputTextBase } from "@components/input-text-base"
import classNames from "classnames"
import React, { forwardRef, type TextareaHTMLAttributes } from "react"

export type TextAreaResize = "auto" | "none" | "vertical"

export type TextAreaSize = "lg" | "md"

interface TextAreaBaseProps {
  /** Helper text displayed below the textarea. Styled as error text when `invalid`. */
  description?: string
  /** Whether the textarea is in an invalid state. */
  invalid?: boolean
  /** Controls resize behavior. */
  resize?: TextAreaResize
  /** Visible height in lines. */
  rows?: number
  /** The size of the textarea. */
  size?: TextAreaSize
}

interface TextAreaWithVisibleLabel extends TextAreaBaseProps {
  /** If true, the label is visually hidden and set as aria-label. */
  hideLabel?: false
  /** The label text for the textarea. */
  label: React.ReactNode
}

interface TextAreaWithHiddenLabel extends TextAreaBaseProps {
  /** If true, the label is visually hidden and set as aria-label. */
  hideLabel: true
  /** The label text for the textarea. Must be a string when hidden. */
  label: string
}

interface TextAreaWithAriaLabelledBy extends TextAreaBaseProps {
  /** ID of an external label element. Use instead of `label`. */
  "aria-labelledby": string
  hideLabel?: never
  label?: never
}

export type TextAreaCustomProps =
  | TextAreaWithAriaLabelledBy
  | TextAreaWithHiddenLabel
  | TextAreaWithVisibleLabel

export type TextAreaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  keyof TextAreaCustomProps | "rows"
> &
  TextAreaCustomProps

/**
 * A multi-line text input that renders as an HTML `<textarea>` with a
 * label and optional description. Supports invalid state, `lg`/`md`
 * sizing, and configurable resize behavior (`vertical`, `none`, `auto`).
 *
 * @component
 * @see {@link https://planningcenter.github.io/tapestry/?path=/docs/components-textarea--docs | Storybook Documentation}
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function TextArea(
    {
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      className,
      description,
      hideLabel,
      id,
      invalid,
      label,
      resize = "vertical",
      rows = 3,
      size = "md",
      ...restProps
    }: TextAreaProps,
    ref
  ) {
    const wrapperClassName = classNames(
      {
        "tds-textarea--resize-auto": resize === "auto",
        "tds-textarea--resize-none": resize === "none",
        "tds-textarea--resize-vertical": resize === "vertical",
      },
      className
    )

    return (
      <InputTextBase
        className={wrapperClassName}
        description={description}
        hideLabel={hideLabel || !!ariaLabelledBy}
        id={id}
        invalid={invalid}
        label={label}
        size={size}
      >
        {({ descriptionId, inputId }) => (
          <textarea
            {...restProps}
            aria-describedby={descriptionId}
            aria-invalid={invalid || undefined}
            aria-label={hideLabel ? ariaLabel || (label as string) : ariaLabel}
            aria-labelledby={ariaLabelledBy}
            id={inputId}
            ref={ref}
            rows={rows}
          />
        )}
      </InputTextBase>
    )
  }
)

TextArea.displayName = "TextArea"
